// ─────────────────────────────────────────────────────────────────────────────
// Pure, dependency-free helpers + types for the Performance Scores dashboard.
// Mirrors the persisted HrPerformanceScore shape (api-hr-performance-scores.js).
// Grade/scale presentation + summary aggregation live here so they're unit-
// testable under `node --test`, keeping the page component thin.
// ─────────────────────────────────────────────────────────────────────────────

export interface ScoreBreakdownItem {
  key: string;
  label: string;
  weight: number;
  value: number;
  points: number;
  hasData: boolean;
}

export interface ScoreEmployeeRef {
  _id?: string;
  display_name?: string;
  department?: string;
}

export interface PerformanceScore {
  _id: string;
  employeeId: ScoreEmployeeRef | string | null;
  period: string;
  department?: string;
  track: 'sales' | 'engineering';
  score: number;
  grade: string;
  rawScore: number;
  warningsCount: number;
  warningPenalty: number;
  breakdown: ScoreBreakdownItem[];
  metrics?: Record<string, number>;
  hasData: boolean;
  computedAt?: string;
}

export interface ScoreSummary {
  count: number;
  average: number;
  topPerformer: { name: string; score: number } | null;
  byDepartment: { department: string; average: number; count: number }[];
}

/** Current calendar month as "YYYY-MM". */
export function getCurrentPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Resolve the (possibly populated) employee display name from a score row. */
export function scoreEmployeeName(score: Pick<PerformanceScore, 'employeeId'>): string {
  const e = score?.employeeId;
  if (e && typeof e === 'object') return (e.display_name ?? 'Employee').trim() || 'Employee';
  return 'Employee';
}

/** Resolve the employee department (populated ref first, then denormalized). */
export function scoreEmployeeDept(score: Pick<PerformanceScore, 'employeeId' | 'department'>): string {
  const e = score?.employeeId;
  const dept = (e && typeof e === 'object' ? e.department : '') || score?.department || '';
  return String(dept).toLowerCase();
}

/** Badge classes for a letter grade (semantic color scale). */
export function gradeBadgeClass(grade: string): string {
  switch (grade) {
    case 'A+':
    case 'A':
      return 'bg-emerald-100 text-emerald-800';
    case 'B':
      return 'bg-lime-100 text-lime-800';
    case 'C':
      return 'bg-amber-100 text-amber-800';
    case 'D':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-red-100 text-red-800';
  }
}

/** Fill color for a 0–100 score/progress bar. */
export function scoreBarClass(score: number): string {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-lime-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

/** Clamp a value into the 0–100 bar width range. */
export function barWidthPct(value: number): number {
  const n = Number(value) || 0;
  return Math.max(0, Math.min(100, n));
}

/**
 * Aggregate a set of scores into dashboard summary stats: overall average,
 * top performer, and per-department averages (sorted by department). Pure and
 * total — an empty list yields a zeroed summary.
 */
export function summarizeScores(scores: readonly PerformanceScore[] | undefined | null): ScoreSummary {
  const list = (scores ?? []).filter(Boolean);
  if (list.length === 0) return { count: 0, average: 0, topPerformer: null, byDepartment: [] };

  let total = 0;
  let top: PerformanceScore | null = null;
  const byDept = new Map<string, { total: number; count: number }>();

  for (const s of list) {
    const score = Number(s.score) || 0;
    total += score;
    if (!top || score > (Number(top.score) || 0)) top = s;

    const dept = scoreEmployeeDept(s) || 'other';
    const bucket = byDept.get(dept) ?? { total: 0, count: 0 };
    bucket.total += score;
    bucket.count += 1;
    byDept.set(dept, bucket);
  }

  const byDepartment = [...byDept.entries()]
    .map(([department, b]) => ({ department, average: Math.round(b.total / b.count), count: b.count }))
    .sort((a, b) => a.department.localeCompare(b.department));

  return {
    count: list.length,
    average: Math.round(total / list.length),
    topPerformer: top ? { name: scoreEmployeeName(top), score: Number(top.score) || 0 } : null,
    byDepartment,
  };
}
